/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface PositionReportAlertProps {
  id: string;
  vehicleId: string;
  hasReceivedQualifyingReport: boolean;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class PositionReportAlertRecord extends Record<PositionReportAlertProps>({
  id: null as any,
  vehicleId: null as any,
  hasReceivedQualifyingReport: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    vehicleId: string;
    hasReceivedQualifyingReport: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): PositionReportAlertRecord {
    if (json == null) {
      throw new Error('PositionReportAlertRecord.fromJS: parameter cannot be null');
    }
    return new PositionReportAlertRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'PositionReportAlertJson.id',
      ),
      vehicleId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
        'PositionReportAlertJson.vehicleId',
      ),
      hasReceivedQualifyingReport: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'hasReceivedQualifyingReport', false),
        'PositionReportAlertJson.hasReceivedQualifyingReport',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'PositionReportAlertJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    vehicleId: string;
    hasReceivedQualifyingReport: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [PositionReportAlertRecord, Error[]] {
    if (json == null) {
      throw new Error('PositionReportAlertRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new PositionReportAlertRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'PositionReportAlertJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
            'PositionReportAlertJson.vehicleId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      hasReceivedQualifyingReport: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'hasReceivedQualifyingReport', false),
            'PositionReportAlertJson.hasReceivedQualifyingReport',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'PositionReportAlertJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: PositionReportAlertProps) {
    super(props);
    if (props == null) {
      throw new Error('PositionReportAlertRecord constructor parameter cannot be null');
    }
  }
}

export abstract class PositionReportAlertViewModelBase implements PositionReportAlertProps {
  protected readonly _props: PositionReportAlertProps;
  private _json?: PositionReportAlertProps;

  get id(): string {
    return this._props.id;
  }
  get vehicleId(): string {
    return this._props.vehicleId;
  }
  get hasReceivedQualifyingReport(): boolean {
    return this._props.hasReceivedQualifyingReport;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: PositionReportAlertProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        vehicleId: this._props.vehicleId,
        hasReceivedQualifyingReport: this._props.hasReceivedQualifyingReport,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllPositionReportAlertEntities = (state: AppState) =>
  state.entityStores.positionReportAlert.entities;

export const selectPositionReportAlertById = (id: string) => (state: AppState) =>
  state.entityStores.positionReportAlert.entities.get(id);

export const selectPositionReportAlertMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.positionReportAlert.meta.get(id);

export const selectPositionReportAlertAndMetadataById = (id: string) =>
  createSelector(
    selectPositionReportAlertById(id),
    selectPositionReportAlertMetadataById(id),
    (positionReportAlert, meta) => ({ positionReportAlert, meta }),
  );

export function selectPositionReportAlertEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllPositionReportAlertEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, PositionReportAlertRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectPositionReportAlertEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllPositionReportAlertEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectPositionReportAlertEntitiesByHasReceivedQualifyingReport(
  hasReceivedQualifyingReport: boolean,
) {
  return createSelector(selectAllPositionReportAlertEntities, (entities) =>
    entities
      .filter((e) => e.hasReceivedQualifyingReport === hasReceivedQualifyingReport)
      .valueSeq(),
  );
}

export function selectPositionReportAlertEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllPositionReportAlertEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
